import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

type Plan = {
  key: string;
  eyebrow: string;
  title: string;
  icon: string;
  price: string;
  priceNote: string;
  perDriver: string;
  stats: { value: string; label: string }[];
  points: string[];
  cta: string;
  href: string;
  dark?: boolean;
};

/**
 * Both products are shown on the same footing: a starting monthly price, then the same three
 * facts (drivers, orders, hubs) and the per-driver rate as supporting detail. This avoids a
 * flat "$49" sitting next to a per-driver "$19" and reading as the cheaper plan.
 */
const PLANS: Plan[] = [
  {
    key: 'lite',
    eyebrow: 'Geofleet Lite',
    title: 'Single-hub delivery',
    icon: 'ri-store-2-line',
    price: '$49',
    priceNote: 'or $39/month billed annually',
    perDriver: '3 drivers included · extra drivers $10/month',
    stats: [
      { value: '3', label: 'Drivers' },
      { value: '500', label: 'Orders / month' },
      { value: 'One', label: 'Hub' },
    ],
    points: [
      'Orders, inventory, live tracking and proof of delivery',
      'Route planning, auto-allocation and WhatsApp from Lite Plus ($99)',
      'AI Copilot and Autopilot on Lite Autopilot ($179)',
    ],
    cta: 'See Lite pricing',
    href: '/pricing/lite',
  },
  {
    key: 'command',
    eyebrow: 'Geofleet Command',
    title: 'Multi-hub logistics',
    icon: 'ri-node-tree',
    price: '$190',
    priceNote: 'billed quarterly',
    perDriver: '10 drivers included · extra drivers $19/month',
    stats: [
      { value: '10', label: 'Drivers' },
      { value: '5,000', label: 'Orders / month' },
      { value: 'Multiple', label: 'Hubs' },
    ],
    points: [
      'Multi-hub operations with route optimization',
      'Shopify, WooCommerce, API and Autopilot',
      'AI dispatch from Premium AI, full agent suite on Enterprise',
    ],
    cta: 'See Command pricing',
    href: '/pricing',
    dark: true,
  },
];

export default function PricingTeaser() {
  const { t } = useTranslation();

  return (
    <section className="bg-[#f4f7fb] py-20 lg:py-24" aria-labelledby="pricing-teaser-title">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('home.pricingTeaser.eyebrow')}</p>
          <h2 id="pricing-teaser-title" className="mt-2 text-3xl font-bold tracking-tight text-slate-950 lg:text-4xl">{t('home.pricingTeaser.title')}</h2>
          <p className="mt-4 leading-relaxed text-slate-600">{t('home.pricingTeaser.subtitle')}</p>
        </div>

        <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-2">
          {PLANS.map((plan) => {
            const d = plan.dark;
            return (
              <article
                key={plan.key}
                className={`flex flex-col rounded-3xl p-7 sm:p-8 ${d ? 'bg-[#071523] text-white shadow-xl shadow-slate-950/10' : 'border border-slate-200 bg-white shadow-sm'}`}
              >
                <div className="flex items-start justify-between gap-5">
                  <div>
                    <p className={`text-xs font-bold uppercase tracking-[0.16em] ${d ? 'text-blue-300' : 'text-blue-700'}`}>{plan.eyebrow}</p>
                    <h3 className={`mt-2 text-2xl font-bold tracking-tight ${d ? '' : 'text-slate-950'}`}>{plan.title}</h3>
                  </div>
                  <span className={`flex h-11 w-11 items-center justify-center rounded-2xl text-xl ${d ? 'bg-white/10 text-blue-300' : 'bg-blue-50 text-[#1769ff]'}`} aria-hidden>
                    <i className={plan.icon} />
                  </span>
                </div>

                <p className={`mt-6 text-xs font-semibold uppercase tracking-wider ${d ? 'text-slate-400' : 'text-slate-500'}`}>Starts at</p>
                <p className="mt-1 flex flex-wrap items-baseline gap-x-1.5">
                  <span className={`text-4xl font-bold tracking-tight ${d ? '' : 'text-slate-950'}`}>{plan.price}</span>
                  <span className={`text-sm ${d ? 'text-slate-400' : 'text-slate-500'}`}>/month</span>
                  <span className={`ms-1 text-sm ${d ? 'text-slate-400' : 'text-slate-500'}`}>{plan.priceNote}</span>
                </p>
                <p className={`mt-2 text-sm ${d ? 'text-slate-300' : 'text-slate-600'}`}>{plan.perDriver}</p>

                <dl className={`mt-6 grid grid-cols-3 gap-2 rounded-2xl p-3 ${d ? 'bg-white/[0.06]' : 'bg-slate-50'}`}>
                  {plan.stats.map((s) => (
                    <div key={s.label} className="flex flex-col-reverse px-2 py-1 text-center">
                      <dt className={`text-[11px] ${d ? 'text-slate-400' : 'text-slate-500'}`}>{s.label}</dt>
                      <dd className={`m-0 text-lg font-bold ${d ? 'text-white' : 'text-slate-950'}`}>{s.value}</dd>
                    </div>
                  ))}
                </dl>

                <ul className="m-0 mt-6 flex-1 list-none space-y-3 p-0">
                  {plan.points.map((point) => (
                    <li key={point} className={`flex items-start gap-2.5 text-sm ${d ? 'text-slate-300' : 'text-slate-600'}`}>
                      <i className={`ri-check-line mt-0.5 ${d ? 'text-blue-300' : 'text-[#1769ff]'}`} aria-hidden />
                      {point}
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.href}
                  className={`mt-7 inline-flex items-center justify-center gap-1.5 rounded-xl px-5 py-3 text-sm font-semibold text-white transition-colors ${d ? 'bg-[#1769ff] hover:bg-[#0f5bd7]' : 'bg-slate-900 hover:bg-slate-800'}`}
                >
                  {plan.cta} <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
                </Link>
              </article>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <Link to="/pricing" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800">{t('home.pricingTeaser.compare')}<i className="ri-arrow-right-line rtl:rotate-180" aria-hidden /></Link>
        </div>
      </div>
    </section>
  );
}
